/**
 * Pricing API utilities for fetching country-based pricing
 * Calls the getCountryInfo Cloud Function to get user's country and pricing
 */

export interface TierPricing {
  currency: string;
  monthly: number;
  annual: number;
}

export interface CountryInfo {
  countryCode: string;
  currency: string;
  /**
   * VAT rate in percent (e.g. 20 for France). 0 for non-EU countries that BV
   * does not charge tax to. Used to render TTC (tax-inclusive) prices on the
   * pricing surface, per French Consumer Code Article L112-1.
   */
  vatRate: number;
  tiers: {
    [productCode: string]: TierPricing;
  };
}

// Cache key bumped after the endpoint shape changed (callable → onRequest).
// v3: Pro tier added.
// v4: vatRate added so prices can be displayed TTC (tax-inclusive) per L112-1.
// v5: invalidate after AR currency-override rollout (ARS → USD) so newsletter
// recipients see the new $19.99 Pro price instead of stale ARS amounts.
// Any stale entries under earlier keys are ignored.
const COUNTRY_INFO_CACHE_KEY = "bv_country_info_v5";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h — matches blindvalet-ng's PricingCatalogService

// Use different URLs for development vs production
const API_BASE_URL =
  process.env.NODE_ENV === "development"
    ? "https://us-central1-blindvalet-develop.cloudfunctions.net"
    : "https://us-central1-blindvalet-production.cloudfunctions.net";

/**
 * Get country info from Cloud Function with caching
 * Returns the user's country code, currency, and pricing tiers
 */
export async function getCountryInfo(): Promise<CountryInfo> {
  // Only run in browser
  if (typeof window === "undefined") {
    return getDefaultCountryInfo();
  }

  // Check cache first
  try {
    const cached = localStorage.getItem(COUNTRY_INFO_CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_TTL_MS) {
        return data;
      }
    }
  } catch (e) {
    // Ignore cache errors
  }

  try {
    // Fetch from Cloud Function. (Renamed from `getCountryInfo` to `pricingInfo`
    // — see functions/index.js for context.)
    const response = await fetch(`${API_BASE_URL}/pricingInfo`);

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data: CountryInfo = await response.json();

    // Cache result
    try {
      localStorage.setItem(
        COUNTRY_INFO_CACHE_KEY,
        JSON.stringify({
          data,
          timestamp: Date.now(),
        })
      );
    } catch (e) {
      // Ignore cache errors
    }

    return data;
  } catch (error) {
    console.error("Failed to fetch country info:", error);
    return getDefaultCountryInfo();
  }
}

/**
 * Get default country info (fallback when API fails or SSR)
 */
function getDefaultCountryInfo(): CountryInfo {
  return {
    countryCode: "US",
    currency: "USD",
    vatRate: 0,
    tiers: {
      kitchen_table: {
        currency: "USD",
        monthly: 0,
        annual: 0,
      },
      home_game: {
        currency: "USD",
        monthly: 9.99,
        annual: 99.99,
      },
      club: {
        currency: "USD",
        monthly: 17.99,
        annual: 179.99,
      },
      pro: {
        currency: "USD",
        monthly: 69.99,
        annual: 699.99,
      },
    },
  };
}

/**
 * Synchronous cache reader. Returns the cached CountryInfo if fresh, or null.
 * Use this to populate React state on first render without a Promise round-trip
 * — avoids the brief default-pricing flash that an async fetch causes.
 */
export function getCachedCountryInfo(): CountryInfo | null {
  if (typeof window === "undefined") return null;
  try {
    const cached = localStorage.getItem(COUNTRY_INFO_CACHE_KEY);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

/**
 * Clear the cached country info
 * Useful when user explicitly wants to refresh pricing
 */
export function clearCountryInfoCache(): void {
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem(COUNTRY_INFO_CACHE_KEY);
    } catch (e) {
      // Ignore errors
    }
  }
}
